import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group"
import { cn } from "../lib/utils"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import { resolvedText } from "./property-value-utils"

function resolveBoolean(value: unknown, resolveSymbol: (value: string) => unknown): boolean {
  const resolved = typeof value === "string" ? resolveSymbol(value) : value
  return resolved === true || resolved === "true"
}

function sizeClass(value: unknown): string {
  switch (String(value ?? "default")) {
    case "xs": return "size-3"
    case "sm": return "size-3.5"
    case "lg": return "size-5"
    case "xl": return "size-6"
    default: return "size-4"
  }
}

export function RadioGroupRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext()
  const directValue = element.value !== undefined
    ? resolvedText(element.value, resolveSymbol)
    : undefined
  const defaultValue = element.defaultValue !== undefined
    ? resolvedText(element.defaultValue, resolveSymbol)
    : ""
  const [boundValue, setBoundValue] = useBoundValue<string>(
    element.bind,
    resolveSymbol,
    directValue ?? defaultValue,
    String,
  )
  const controlledValue = element.bind !== undefined ? boundValue : directValue
  const disabled = resolveBoolean(element.disabled, resolveSymbol)
  const label = resolvedText(element.text, resolveSymbol)
  const name = resolvedText((element as unknown as { name?: unknown }).name, resolveSymbol) || undefined

  const group = (
    <RadioGroup
      {...(controlledValue !== undefined
        ? { value: controlledValue }
        : defaultValue
          ? { defaultValue }
          : {})}
      name={name}
      disabled={disabled}
      onValueChange={(value) => {
        if (element.bind !== undefined) setBoundValue(value)
        dispatchEvent(element.id, "change", { ...(itemContext ?? {}), value })
      }}
      className={cn("grid gap-2", element.className)}
      aria-label={element.ariaLabel ?? (label || "Radio group")}
    >
      {(element.children ?? []).map((rawChild) => {
        // JITRULE Task 2: item elements may nest their fields under `props`.
        const child = liftV4Props(rawChild)
        const itemValue = resolvedText(child.value ?? child.defaultValue ?? child.id, resolveSymbol, child.id)
        const itemText = resolvedText(child.text ?? child.label, resolveSymbol)
        const itemDisabled = disabled || resolveBoolean(child.disabled, resolveSymbol)
        return (
          <div
            key={child.id}
            className={cn("flex items-center gap-2", child.className)}
            data-cogo-element-id={child.id}
            data-cogo-type={child.type}
          >
            <RadioGroupItem
              id={child.id}
              value={itemValue}
              disabled={itemDisabled}
              className={sizeClass(child.size)}
              aria-label={itemText || child.ariaLabel}
            />
            {itemText ? <label htmlFor={child.id}>{itemText}</label> : null}
            {child.children?.map((nested) => (
              <ElementRenderer key={nested.id} element={nested} />
            ))}
          </div>
        )
      })}
    </RadioGroup>
  )

  return label
    ? <fieldset disabled={disabled}><legend>{label}</legend>{group}</fieldset>
    : group
}

export function RadioGroupItemRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext()
  const value = resolvedText(element.value ?? element.defaultValue ?? element.id, resolveSymbol, element.id)
  const text = resolvedText(element.text ?? element.label, resolveSymbol)
  const disabled = resolveBoolean(element.disabled, resolveSymbol)
  return (
    <RadioGroup
      defaultValue={element.checked === true ? value : undefined}
      disabled={disabled}
      onValueChange={(next) => dispatchEvent(element.id, "change", { ...(itemContext ?? {}), value: next })}
      aria-label={element.ariaLabel ?? (text || "Radio option")}
    >
      <div
        className={cn("flex items-center gap-2", element.className)}
        data-cogo-element-id={element.id}
        data-cogo-type={element.type}
      >
        <RadioGroupItem id={element.id} value={value} className={sizeClass(element.size)} disabled={disabled} />
        {text ? <label htmlFor={element.id}>{text}</label> : null}
        {element.children?.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </div>
    </RadioGroup>
  )
}
